<template>
    <div
        v-editable="blok"
        class="product-grid"
        :class="blok.style"
        :style="`background-color: ${blok?.BackgroundColor?.color};`"
    >
        <div class="container product-grid-container">
            <div v-if="blok.breadcrumbs" class="breadcrumbs">
                <nuxt-link to="/" external>
                    <NuxtIcon name="ri:home-9-line" />
                </nuxt-link>
                >
                <nuxt-link v-if="getProductType() === 'HotelProductCard'" to="/airport-hotels" external
                    >Airport Hotels</nuxt-link
                >
                <nuxt-link v-if="getProductType() === 'ParkingProductCard'" to="/parking" external>Parking</nuxt-link>
                >
                <span v-if="airport.content"
                    ><strong>{{ airport.content?.code }}</strong></span
                >
            </div>

            <h3 v-if="blok.title" class="product-grid-title">{{ blok.title }}</h3>
            <div v-if="blok.content" class="product-grid-content" v-html="blok.content"></div>
            <div v-if="productList?.length" class="product-grid-body" :class="`product-grid-body-cols-${blok.Columns}`">
                <template v-for="(product, index) in productList" :key="index">
                    <transition name="quick-fade">
                        <Component
                            :is="getProductType()"
                            v-show="toggleMore || index < blok.amount_shown"
                            v-if="product"
                            :blok="{ component: getProductType() }"
                            :product-data="product"
                            :airport-data="airport"
                            :type="blok.productCardsType"
                        />
                    </transition>
                </template>
            </div>
            <div v-else class="no-products">Sorry, we do not have any {{ getProductTypeSimple() }} available.</div>

            <div v-if="productList?.length > blok.amount_shown" class="toggle-more-container">
                <div class="toggle-more" @click="toggleMore = !toggleMore">
                    <template v-if="!toggleMore">
                        Show more products
                        <NuxtIcon name="ri:arrow-down-s-line" class="block" />
                    </template>
                    <template v-else>
                        <NuxtIcon name="ri:arrow-up-s-line" class="block" />
                        Hide products
                    </template>
                </div>
            </div>
            <div v-if="blok.summary_info && products.parkingStats && productList?.length" class="summary-info">
                <table>
                    <tbody>
                        <tr>
                            <th colspan="2">{{ products.parkingStats.title }}</th>
                        </tr>
                        <template v-for="(stat, index) in products.parkingStats.data" :key="index">
                            <tr>
                                <td>{{ stat.title }}</td>
                                <td v-if="stat.title === 'Number of options:'">{{ productList?.length }}</td>
                                <td v-else>{{ stat.content }}</td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
            <div v-if="blok.summary_info && products.airportStats && productList?.length" class="summary-info">
                <airport-stats
                    :blok="blok"
                    :airport="airport"
                    :products="products"
                    :airport-stats="products.airportStats"
                />
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import AirportStats from '@nuxt-monorepo/ui/components/common/AirportStats.vue';
import { filterProducts } from '@nuxt-monorepo/ui/utils/apiFilter';

const { blok, airport, products } = defineProps<{ blok: any; airport: any; products: any }>();

const toggleMore = ref(false);

const productList: any = filterProducts(blok, products);

function getProductType() {
    if (blok.product_type === 'HotelWithParking') {
        return 'HotelProductCard';
    } else if (blok.product_type === 'Hotel') {
        return 'HotelProductCard';
    } else if (blok.product_type === 'Parking') {
        return 'ParkingProductCard';
    }
}

function getProductTypeSimple() {
    if (blok.product_type === 'HotelWithParking') {
        return 'hotels';
    } else if (blok.product_type === 'Hotel') {
        return 'hotels';
    } else if (blok.product_type === 'Parking') {
        return 'parking lots';
    }
}
</script>

<style lang="postcss" scoped>
.product-grid {
    @apply bg-[#f8f9fa];
}
.product-grid-container {
    @apply mx-auto py-12 px-4;
}
.product-grid-title {
    @apply px-4 mb-8 text-center text-lg font-normal leading-[3rem] text-[#1f303d];
    @apply md:text-[40px];
}
.airport .product-grid-title {
    @apply px-4 mb-8 text-center text-2xl font-semibold leading-[2rem] text-[#1f303d];
    @apply md:text-2xl;
}

.product-grid-content {
    @apply px-4 mb-12 text-lg text-center text-[#1f303d];
}
.no-products {
    @apply text-center;
}
.product-grid-body {
    @apply mt-8 mb-4 grid gap-4;
    @apply sm:mb-8;
}
.product-grid-item {
    @apply relative flex items-center justify-center w-full bg-[#32434c] rounded z-[0];
    @apply text-2xl font-semibold text-white text-center;
    height: 100px;

    @media (min-width: theme('screens.sm')) {
        height: 250px;
    }

    &:hover {
        .image-grid-item-overlay {
            @apply opacity-50;
        }
        .image-grid-item-text {
            @apply underline;
        }
    }
}
.product-grid-item-bg {
    @apply absolute top-0 left-0 bottom-0 right-0 bg-center bg-no-repeat bg-cover rounded opacity-50;
    z-index: 2;
}
.product-grid-item-overlay {
    @apply absolute top-0 left-0 bottom-0 right-0 rounded opacity-0 transition-opacity;
    z-index: 3;
}
.product-grid-item-text {
    @apply absolute;
    z-index: 4;
}

.toggle-more-container {
    @apply flex justify-center;
    .toggle-more {
        @apply flex items-center mx-auto text-center text-primary font-semibold cursor-pointer;
        .iconify {
            @apply mx-1;
        }
    }
}

.breadcrumbs {
    @apply text-sm;
    a {
        @apply mx-1 hover:underline;
    }
    strong {
        @apply font-semibold;
    }
}
.summary-info {
    table {
        @apply text-sm;
        th {
            @apply pb-2 text-left font-semibold;
        }
        td {
            @apply pb-1;
        }
        tbody tr td:first-child {
            width: 160px;
        }
    }
}

/* find the way to replace with for, etc */
.product-grid-body-cols-1 {
    @apply md:grid-cols-1;
}
.product-grid-body-cols-2 {
    @apply md:grid-cols-2;
}
.product-grid-body-cols-3 {
    @apply md:grid-cols-3;
}
.product-grid-body-cols-4 {
    @apply md:grid-cols-4;
}
.product-grid-body-cols-5 {
    @apply md:grid-cols-5;
}
.product-grid-body-cols-6 {
    @apply md:grid-cols-6;
}
.product-grid-body-cols-7 {
    @apply md:grid-cols-7;
}
.product-grid-body-cols-8 {
    @apply md:grid-cols-8;
}
.product-grid-body-cols-9 {
    @apply md:grid-cols-9;
}
.product-grid-body-cols-10 {
    @apply md:grid-cols-10;
}
</style>
